import React from 'react';

import type { NextPageContext } from 'Src/types/next-serverside';
import type { CMSContent } from 'Src/types/common/cms';
import domainsService from '@clubmed/domains-service';

import { STATUS_CODE_500, STATUS_CODE_404 } from 'Utils/status-code/statusCode.constants';

import { getCurrentLocale } from 'Utils/context/contextUtils';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { PAGE_404 } from 'Utils/request/requestDictionary';

import { createAppContextFromReq } from 'Server/context/createAppContextFromReq';
import { getAppContext } from 'Context/createOrUpdateAppContext';

import ServerErrorView from 'Views/errors/internal-server-error/InternalServerErrorView';
import NotFoundView from 'Views/errors/not-found/NotFoundView';

const QUERY_404 = `query {
  config {
    errors {
      notFound {
        description
        image
        items {
          backgroundURL
          label
          link
        }
        title
      }
    }
    common { goToHome }
  }
}`;

export const notFoundInitialProps = async (req: any) => {
  const appContext = req ? await createAppContextFromReq(req) : getAppContext();

  return graphQLClient.runQuery(QUERY_404, appContext, { requestName: PAGE_404 });
};

type Props = {
  statusCode: number;
  config?: unknown;
  error?: unknown;
  [key: string]: unknown;
};

const getHost = (req: any): string => {
  if (typeof window !== 'undefined') {
    return document.location.host;
  }

  if (req.headers['x-forwarded-host']) {
    return req.headers['x-forwarded-host'];
  }

  return req.get('host') ?? req.headers.host ?? 'localhost:3000';
};

const getProtocol = (req: any): string => {
  if (typeof window !== 'undefined') {
    return document.location.protocol;
  }

  const protocol = req.headers['x-forwarded-proto'] ?? req.protocol;

  if (!protocol) {
    return 'https:';
  }

  if (!protocol.endsWith(':')) {
    return `${protocol}:`;
  }

  return protocol;
};

const getHostname = (req: any): string => {
  if (typeof window !== 'undefined') {
    return document.location.hostname;
  }

  return new URL(`${getProtocol(req)}//${getHost(req)}`).hostname;
};

const ErrorPage = ({ statusCode, config, error, ...props }: Props) => {
  if (statusCode === STATUS_CODE_404) {
    return <NotFoundView config={config as CMSContent} {...props} />;
  }

  return <ServerErrorView error={error as InternalServerError} {...props} />;
};

ErrorPage.pageId = 'ErrorPage';

ErrorPage.getInitialProps = async ({ req, res, query, err }: NextPageContext) => {
  if (
    res?.statusCode === STATUS_CODE_404 ||
    err?.statusCode === STATUS_CODE_404 ||
    query.statusCode === STATUS_CODE_404.toString()
  ) {
    const props = await notFoundInitialProps(req);

    res?.status(STATUS_CODE_404);

    return {
      ...props,
      statusCode: STATUS_CODE_404,
    };
  }

  const hostname = getHostname(req);
  const locale = getCurrentLocale(query.locale, hostname);

  const { error_500: error } = domainsService.getCurrentDomain(locale);

  res?.status(STATUS_CODE_500);

  return {
    error,
    statusCode: STATUS_CODE_500,
  };
};

export default ErrorPage;
